import { useReducer } from 'react';
import type {
  Analysis,
  HealthInfo,
  Loop,
  Mode,
  QuantizeMeta,
  Selection,
  Status,
  Track,
} from '../types';
import { uid } from '../format';

const MODE_KEY = 'quantime.mode';
const MAX_HISTORY = 8;

export interface State {
  mode: Mode;
  status: Status;
  name: string;
  buffer: AudioBuffer | null;
  /** Version d'avant quantification, pour l'écoute comparative. */
  preQuant: AudioBuffer | null;
  showOriginal: boolean;
  quantized: boolean;
  analysis: Analysis | null;
  quantMeta: QuantizeMeta | null;
  bpm: number;
  division: number;
  strength: number;
  quantMode: 'warp' | 'slice';
  selectionOnly: boolean;
  nrAmount: number;
  useHighpass: boolean;
  useHum: boolean;
  selection: Selection | null;
  loops: Loop[];
  activeLoop: string | null;
  /** Pistes empilées (couches) — conservées quand on enregistre/importe une nouvelle prise. */
  tracks: Track[];
  view: Selection;
  busy: string | null;
  error: string | null;
  health: HealthInfo | null;
  serverOk: boolean | null;
  history: AudioBuffer[];
}

function readMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === 'maestro' ? 'maestro' : 'troubadour';
  } catch {
    return 'troubadour';
  }
}

export const initialState: State = {
  mode: readMode(),
  status: 'empty',
  name: '',
  buffer: null,
  preQuant: null,
  showOriginal: false,
  quantized: false,
  analysis: null,
  quantMeta: null,
  bpm: 0,
  division: 0.25,
  strength: 1,
  quantMode: 'warp',
  selectionOnly: false,
  nrAmount: 0.6,
  useHighpass: true,
  useHum: true,
  selection: null,
  loops: [],
  activeLoop: null,
  tracks: [],
  view: { start: 0, end: 0 },
  busy: null,
  error: null,
  health: null,
  serverOk: null,
  history: [],
};

export type Action =
  | { type: 'mode'; mode: Mode }
  | { type: 'patch'; patch: Partial<State> }
  | { type: 'loaded'; buffer: AudioBuffer; name: string }
  | { type: 'analysis'; analysis: Analysis }
  | { type: 'replace'; buffer: AudioBuffer; quantized?: boolean; meta?: QuantizeMeta | null }
  | { type: 'undo' }
  | { type: 'loop/add'; start: number; end: number }
  | { type: 'loop/remove'; id: string }
  | { type: 'loop/active'; id: string | null }
  | { type: 'loop/mix'; id: string; mixed: boolean }
  | { type: 'loop/move'; id: string; edge: 'start' | 'end'; delta: number }
  | { type: 'track/add'; buffer: AudioBuffer; name: string }
  | { type: 'track/remove'; id: string }
  | { type: 'track/patch'; id: string; patch: Partial<Pick<Track, 'volume' | 'muted' | 'name'>> }
  | { type: 'reset' };

export function reducer(s: State, a: Action): State {
  switch (a.type) {
    case 'mode': {
      try {
        localStorage.setItem(MODE_KEY, a.mode);
      } catch {
        /* mode privé : on continue sans persister */
      }
      return { ...s, mode: a.mode };
    }

    case 'patch':
      return { ...s, ...a.patch };

    case 'loaded':
      return {
        ...s,
        status: 'ready',
        name: a.name,
        buffer: a.buffer,
        preQuant: null,
        showOriginal: false,
        quantized: false,
        analysis: null,
        quantMeta: null,
        bpm: 0,
        selection: null,
        loops: [],
        activeLoop: null,
        view: { start: 0, end: a.buffer.duration },
        history: [],
        error: null,
        busy: null,
      };

    case 'analysis':
      return {
        ...s,
        analysis: a.analysis,
        bpm: s.bpm || (a.analysis.bpm ?? 0),
      };

    case 'replace': {
      const history = s.buffer ? [s.buffer, ...s.history].slice(0, MAX_HISTORY) : s.history;
      const dur = a.buffer.duration;
      return {
        ...s,
        buffer: a.buffer,
        history,
        preQuant: a.quantized ? (s.preQuant ?? s.buffer) : s.preQuant,
        quantized: a.quantized ? true : s.quantized,
        quantMeta: a.meta ?? s.quantMeta,
        showOriginal: false,
        busy: null,
        error: null,
        // La durée peut changer d'un cheveu après un warp : on recadre.
        view: { start: Math.min(s.view.start, dur), end: Math.min(s.view.end || dur, dur) },
        loops: s.loops.map((l) => ({
          ...l,
          start: Math.min(l.start, dur),
          end: Math.min(l.end, dur),
        })),
      };
    }

    case 'undo': {
      const [prev, ...rest] = s.history;
      if (!prev) return s;
      return {
        ...s,
        buffer: prev,
        history: rest,
        quantized: rest.length ? s.quantized : false,
        showOriginal: false,
        error: null,
      };
    }

    case 'loop/add': {
      const n = s.loops.length + 1;
      const loop: Loop = {
        id: uid(),
        name: `Boucle ${n}`,
        start: a.start,
        end: a.end,
        mixed: true,
      };
      return { ...s, loops: [...s.loops, loop], activeLoop: loop.id };
    }

    case 'loop/remove': {
      const loops = s.loops.filter((l) => l.id !== a.id);
      return {
        ...s,
        loops,
        activeLoop: s.activeLoop === a.id ? (loops[loops.length - 1]?.id ?? null) : s.activeLoop,
      };
    }

    case 'loop/active':
      return { ...s, activeLoop: a.id };

    case 'loop/mix':
      return {
        ...s,
        loops: s.loops.map((l) => (l.id === a.id ? { ...l, mixed: a.mixed } : l)),
      };

    case 'loop/move': {
      const dur = s.buffer?.duration ?? 0;
      return {
        ...s,
        loops: s.loops.map((l) => {
          if (l.id !== a.id) return l;
          if (a.edge === 'start') {
            const start = Math.max(0, Math.min(l.end - 0.05, l.start + a.delta));
            return { ...l, start };
          }
          const end = Math.min(dur, Math.max(l.start + 0.05, l.end + a.delta));
          return { ...l, end };
        }),
      };
    }

    case 'track/add': {
      const track: Track = { id: uid(), name: a.name, buffer: a.buffer, volume: 1, muted: false };
      return { ...s, tracks: [...s.tracks, track] };
    }

    case 'track/remove':
      return { ...s, tracks: s.tracks.filter((t) => t.id !== a.id) };

    case 'track/patch':
      return {
        ...s,
        tracks: s.tracks.map((t) => (t.id === a.id ? { ...t, ...a.patch } : t)),
      };

    case 'reset':
      return { ...initialState, mode: s.mode, health: s.health, serverOk: s.serverOk };

    default:
      return s;
  }
}

export function useSession() {
  return useReducer(reducer, initialState);
}
